import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "redux";
import { injectIntl } from "react-intl";
import PropTypes from "prop-types";
import bindAll from "lodash.bindall";
import bowser from "bowser";
import React from "react";
import toast from "react-hot-toast";
import { Save, FolderOpen, Plus, LogIn, FilePlus } from "lucide-react";
import VM from "scratch-vm";

import Box from "../box/box.jsx";
import MenuBarHOC from "../../containers/menu-bar-hoc.jsx";

import { requestNewProject } from "../../reducers/project-state";
import { setFileHandle } from "../../reducers/tw.js";
import { setProjectUnchanged } from "../../reducers/project-changed";
import { setProjectTitle } from "../../reducers/project-title";
import {
    setCurrentProject,
    clearCurrentProject,
    setProjectsList,
    removeProject,
} from "../../reducers/magblocks-project";

import {
    createProject,
    updateProject,
    listProjects,
    fetchProject,
    deleteProject,
    buildSb3,
    exportMgnm,
    importMgnm,
    UnauthorizedError,
} from "../../lib/magblocks-api.js";

import Modal from "../../containers/modal.jsx";
import modalStyles from "./magblocks-modal.css";
import MagblocksSaveModal from "./magblocks-save-modal.jsx";
import MagblocksLoadModal from "./magblocks-load-modal.jsx";
import MagblocksAuthModal from "./magblocks-auth-modal.jsx";
import UserMenu from "./magblocks-user-menu.jsx";

import styles from "./magblocks-menu-bar.css";
import magnumLogo from "./logo.svg";

const APP_URL = process.env.MAGNUM_APP_URL;
const SIGN_IN_URL = `${APP_URL}/en/sign-in?redirect=magblocks`;

class MagblocksMenuBar extends React.Component {
    constructor(props) {
        super(props);
        bindAll(this, [
            "handleClickNew",
            "handleNewModalConfirm",
            "handleNewModalCancel",
            "handleClickSave",
            "handleClickLoad",
            "handleImportFileChange",
            "handleKeyPress",
            "handleSaveModalCloudSave",
            "handleSaveModalExport",
            "handleSaveModalUpdate",
            "handleSaveModalSaveAsNew",
            "handleNameChange",
            "handleNameSubmit",
            "handleNameBack",
            "handleLoadModalCloudLoad",
            "handleLoadModalImport",
            "handleLoadModalBack",
            "handleLoadSelect",
            "handleLoadDelete",
            "handleModalCancel",
            "handleAuthSignIn",
            "handleAuthCancel",
            "handleAuthMessage",
        ]);
        this.state = {
            // 'hidden' | 'options' | 'name' | 'update'
            saveView: "hidden",
            prevSaveView: null,
            // 'hidden' | 'options' | 'projects'
            loadView: "hidden",
            showAuthModal: false,
            showNewModal: false,
            // which action triggered the auth modal — 'save' | 'load'
            authPendingAction: null,
            authPending: false,
            nameValue: "",
            nameError: null,
            isSaving: false,
            isLoadingList: false,
            deletingId: null,
        };
        this._importInput = null;
        this._authPopup = null;
        this._pollTimer = null;
    }
    componentDidMount() {
        document.addEventListener("keydown", this.handleKeyPress);
        window.addEventListener("message", this.handleAuthMessage);
    }
    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyPress);
        window.removeEventListener("message", this.handleAuthMessage);
        this._clearAuthWatchers();
    }
    handleKeyPress(event) {
        const modifier = bowser.mac ? event.metaKey : event.ctrlKey;
        if (modifier && event.key.toLowerCase() === "s") {
            event.preventDefault();
            this.handleClickSave();
        }
        if (event.key === "Escape") {
            this.handleModalCancel();
        }
    }
    handleClickNew() {
        if (this.props.currentProjectId || this.props.projectChanged) {
            this.setState({showNewModal: true});
        } else {
            this.props.onClickNew();
        }
    }
    handleNewModalConfirm() {
        this.setState({showNewModal: false});
        this.props.onClickNew();
    }
    handleNewModalCancel() {
        this.setState({showNewModal: false});
    }

    // ─── Cloud Save ───────────────────────────────────────────────────────────

    handleClickSave() {
        if (!this.props.isLoggedIn) {
            this.setState({ showAuthModal: true, authPendingAction: "save" });
            return;
        }
        // Always show the options modal first (cloud vs export)
        this.setState({ saveView: "options", nameValue: "", nameError: null });
    }

    // User picked "Save to My Projects" from options screen
    handleSaveModalCloudSave() {
        if (this.props.currentProjectId) {
            // Already a cloud project — show update/new choice
            this.setState({ saveView: "update" });
        } else {
            // No cloud project yet — need a name
            this.setState({ saveView: "name", nameValue: "", nameError: null });
        }
    }

    // User picked "Save as File" from options screen
    async handleSaveModalExport() {
        this.setState({ saveView: "hidden" });
        const toastId = toast.loading("Preparing file…");
        try {
            const blob = await this.props.saveProjectSb3();
            await exportMgnm(blob, this.props.projectTitle);
            toast.success("File downloaded!", { id: toastId });
        } catch (err) {
            toast.error(err.message, { id: toastId });
        }
    }

    // Update existing cloud project
    async handleSaveModalUpdate() {
        this.setState({ isSaving: true, saveView: "hidden" });
        const toastId = toast.loading("Saving…");
        try {
            const blob = await this.props.saveProjectSb3();
            const result = await updateProject(
                this.props.currentProjectId,
                this.props.currentProjectName || this.props.projectTitle,
                blob,
            );
            this.props.onProjectSaved(result.id, result.name);
            toast.success(`"${result.name}" saved!`, { id: toastId });
        } catch (err) {
            if (err instanceof UnauthorizedError) {
                toast.dismiss(toastId);
                this.setState({ showAuthModal: true });
            } else {
                toast.error(err.message, { id: toastId });
            }
        } finally {
            this.setState({ isSaving: false });
        }
    }

    // "Save as New Project" from the update screen → go to name input
    handleSaveModalSaveAsNew() {
        this.setState({ saveView: "name", prevSaveView: "update", nameValue: "", nameError: null });
    }

    handleNameChange(e) {
        this.setState({ nameValue: e.target.value, nameError: null });
    }

    // Submit the name form — create new cloud project
    async handleNameSubmit() {
        const name = this.state.nameValue.trim();
        if (!name) return;
        this.setState({ isSaving: true, nameError: null });
        try {
            const blob = await this.props.saveProjectSb3();
            const result = await createProject(name, blob);
            this.props.onProjectSaved(result.id, result.name);
            this.setState({ saveView: "hidden" });
            toast.success(`"${result.name}" saved!`);
        } catch (err) {
            if (err instanceof UnauthorizedError) {
                this.setState({ saveView: "hidden", showAuthModal: true });
            } else {
                this.setState({ nameError: err.message });
            }
        } finally {
            this.setState({ isSaving: false });
        }
    }

    handleNameBack() {
        this.setState(prev => ({
            saveView: prev.prevSaveView || "options",
            prevSaveView: null,
            nameError: null
        }));
    }

    // ─── Load ────────────────────────────────────────────────────────────────

    handleClickLoad() {
        if (!this.props.isLoggedIn) {
            this.setState({ showAuthModal: true, authPendingAction: "load" });
            return;
        }
        this.setState({ loadView: "options" });
    }

    // User picked "My Projects" from options screen
    async handleLoadModalCloudLoad() {
        this.setState({ loadView: "projects", isLoadingList: true });
        try {
            const projects = await listProjects();
            this.props.onProjectsListLoaded(projects);
        } catch (err) {
            toast.error(err.message);
            this.setState({ loadView: "hidden" });
        } finally {
            this.setState({ isLoadingList: false });
        }
    }

    // User picked "Import from Device" from options screen
    handleLoadModalImport() {
        this.setState({ loadView: "hidden" });
        if (!this._importInput) return;
        this._importInput.value = "";
        this._importInput.click();
    }

    handleLoadModalBack() {
        this.setState({ loadView: "options" });
    }
    async handleLoadSelect(project) {
        this.setState({ loadView: "hidden" });
        const confirmed = this.props.confirmReadyToReplaceProject(
            "Load this project? Unsaved changes will be lost.",
        );
        if (!confirmed) return;

        const toastId = toast.loading(`Loading "${project.name}"…`);
        try {
            const fetchResult = await fetchProject(project.id);
            const { projectJson, assets } = fetchResult;
            const sb3Buffer = await buildSb3({ projectJson, assets });
            await this.props.vm.loadProject(sb3Buffer);
            this.props.vm.renderer.draw();
            this.props.onProjectLoaded(project.id, project.name);
            toast.success(`"${project.name}" loaded!`, { id: toastId });
        } catch (err) {
            toast.error(err.message, { id: toastId });
        }
    }

    async handleLoadDelete(project) {
        this.setState({ deletingId: project.id });
        try {
            await deleteProject(project.id);
            this.props.onProjectDeleted(project.id);
            toast.success(`"${project.name}" deleted`);
        } catch (err) {
            toast.error(err.message);
        } finally {
            this.setState({ deletingId: null });
        }
    }

    // ─── Local Import (.mgnm) ────────────────────────────────────────────────

    async handleImportFileChange(e) {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        const confirmed = this.props.confirmReadyToReplaceProject(
            "Load this project? Unsaved changes will be lost.",
        );
        if (!confirmed) return;

        const toastId = toast.loading("Importing project…");
        try {
            const blob = await importMgnm(file);
            this.props.vm.quit();
            await this.props.vm.loadProject(blob);
            this.props.vm.renderer.draw();
            this.props.onProjectLoaded(null, file.name.replace(/\.mgnm$/i, ""));
            toast.success(`"${file.name.replace(/\.mgnm$/i, "")}" imported!`, {
                id: toastId,
            });
        } catch (err) {
            toast.error(err.message, { id: toastId });
        }
    }
    handleModalCancel() {
        this.setState({ saveView: "hidden", loadView: "hidden" });
    }
    _clearAuthWatchers() {
        if (this._pollTimer) clearInterval(this._pollTimer);
        this._pollTimer = null;
        this._authPopup = null;
    }
    handleAuthMessage(event) {
        if (new URL(APP_URL).origin !== event.origin) return;
        if (!event.data || event.data.type !== "magblocks:auth:success") return;
        const { user } = event.data;
        if (!user) return;
        this._clearAuthWatchers();
        this.props.onSetAuthUser(user);
        const pendingAction = this.state.authPendingAction;
        this.setState({ showAuthModal: false, authPendingAction: null, authPending: false }, () => {
            toast.success(`Welcome, ${user.name}!`);
            if (pendingAction === 'save') this.handleClickSave();
            else if (pendingAction === 'load') this.handleClickLoad();
        });
    }
    handleAuthSignIn() {
        const w = 560;
        const h = 760;
        const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
        const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
        this._authPopup = window.open(
            SIGN_IN_URL,
            "magblocks-signin",
            `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`,
        );
        this.setState({ authPending: true });

        this._pollTimer = setInterval(() => {
            if (this._authPopup && this._authPopup.closed) {
                this._clearAuthWatchers();
                this.setState({ authPending: false });
            }
        }, 500);
    }
    handleAuthCancel() {
        this.setState({ showAuthModal: false, authPendingAction: null });
    }

    render() {
        const {
            saveView,
            loadView,
            nameValue,
            nameError,
            isSaving,
            isLoadingList,
            authPending,
        } = this.state;

        return (
            <Box className={classNames(this.props.className, styles.menuBar)}>
                {/* Left — logo + breadcrumb */}
                <div className={styles.left}>
                    <a href={`${APP_URL}/en`}>
                        <img
                            alt="Magnum"
                            className={styles.magnumLogo}
                            draggable={false}
                            src={magnumLogo}
                        />
                    </a>
                    <span className={styles.breadcrumbSep}>{"/"}</span>
                    <a
                        className={styles.breadcrumbLink}
                        href={`${APP_URL}/en/ide`}
                    >
                        {"Modes"}
                    </a>
                    <span className={styles.breadcrumbSep}>{"/"}</span>
                    <span className={styles.breadcrumbItem}>{"MagBlocks"}</span>
                </div>

                {/* Center — project name (only show when a project is actually loaded) */}
                {this.props.currentProjectName && (
                    <div className={styles.center}>
                        <span className={styles.projectTitle}>
                            {this.props.currentProjectName}
                        </span>
                    </div>
                )}

                {/* Right — actions */}
                <div className={styles.right}>
                    <button
                        className={styles.menuBtn}
                        disabled={isSaving}
                        onClick={this.handleClickSave}
                    >
                        <Save size={14} strokeWidth={2.5} />
                        {isSaving ? "Saving…" : "Save"}
                    </button>
                    <button
                        className={styles.menuBtn}
                        onClick={this.handleClickLoad}
                    >
                        <FolderOpen size={14} strokeWidth={2.5} />
                        {"Load"}
                    </button>
                    <button
                        className={classNames(
                            styles.menuBtn,
                            styles.menuBtnNew,
                        )}
                        onClick={this.handleClickNew}
                    >
                        <Plus size={14} strokeWidth={2.5} />
                        {"New"}
                    </button>

                    <div className={styles.divider} />

                    {this.props.authLoading ? (
                        <div className={styles.authShimmer} />
                    ) : this.props.isLoggedIn ? (
                        <UserMenu
                            user={this.props.user}
                            onSignOut={this.props.onSignOut}
                        />
                    ) : (
                        <button
                            className={styles.loginBtn}
                            disabled={authPending}
                            onClick={this.handleAuthSignIn}
                        >
                            <LogIn size={14} strokeWidth={2.5} />
                            {authPending ? "Logging in…" : "Login"}
                        </button>
                    )}
                </div>

                {/* Hidden file input for .mgnm import */}
                <input
                    accept=".mgnm"
                    ref={(el) => void (this._importInput = el)}
                    style={{ display: "none" }}
                    type="file"
                    onChange={this.handleImportFileChange}
                />

                {/* Save modal — multi-view */}
                {saveView !== "hidden" && (
                    <MagblocksSaveModal
                        isSaving={isSaving}
                        nameError={nameError}
                        nameValue={nameValue}
                        projectName={
                            this.props.currentProjectName ||
                            this.props.projectTitle
                        }
                        view={saveView}
                        onBack={this.handleNameBack}
                        onCancel={this.handleModalCancel}
                        onCloudSave={this.handleSaveModalCloudSave}
                        onExport={this.handleSaveModalExport}
                        onNameChange={this.handleNameChange}
                        onNameSubmit={this.handleNameSubmit}
                        onSaveAsNew={this.handleSaveModalSaveAsNew}
                        onUpdate={this.handleSaveModalUpdate}
                    />
                )}

                {/* Load modal — multi-view */}
                {loadView !== "hidden" && (
                    <MagblocksLoadModal
                        deletingId={this.state.deletingId}
                        isLoading={isLoadingList}
                        projects={this.props.projects}
                        view={loadView}
                        onBack={this.handleLoadModalBack}
                        onCancel={this.handleModalCancel}
                        onCloudLoad={this.handleLoadModalCloudLoad}
                        onDelete={this.handleLoadDelete}
                        onImport={this.handleLoadModalImport}
                        onSelect={this.handleLoadSelect}
                    />
                )}

                {/* Auth prompt — shown to guests before save/load */}
                {this.state.showAuthModal && (
                    <MagblocksAuthModal
                        onCancel={this.handleAuthCancel}
                        onSignIn={this.handleAuthSignIn}
                    />
                )}

                {/* New project confirmation */}
                {this.state.showNewModal && (
                    <Modal
                        className={modalStyles.modalContent}
                        contentLabel="Start a new project"
                        id="magblocksNew"
                        onRequestClose={this.handleNewModalCancel}
                    >
                        <Box className={modalStyles.modalBody}>
                            <div className={modalStyles.authIcon}>
                                <FilePlus size={32} strokeWidth={2} />
                            </div>
                            <p className={modalStyles.authDescription}>
                                {this.props.currentProjectName
                                    ? `This will close "${this.props.currentProjectName}" and open a new empty project.`
                                    : 'This will clear your current work and open a new empty project.'
                                }
                            </p>
                            <div className={modalStyles.stackActions}>
                                <button
                                    className={modalStyles.btnPrimary}
                                    onClick={this.handleNewModalConfirm}
                                >
                                    {'New Project'}
                                </button>
                                <button
                                    className={modalStyles.btn}
                                    onClick={this.handleNewModalCancel}
                                >
                                    {'Cancel'}
                                </button>
                            </div>
                        </Box>
                    </Modal>
                )}
            </Box>
        );
    }
}

MagblocksMenuBar.propTypes = {
    className: PropTypes.string,
    confirmReadyToReplaceProject: PropTypes.func,
    currentProjectId: PropTypes.string,
    currentProjectName: PropTypes.string,
    projectChanged: PropTypes.bool,
    authLoading: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    user: PropTypes.object,
    onSetAuthUser: PropTypes.func,
    onSignOut: PropTypes.func,
    onClickNew: PropTypes.func,
    onProjectDeleted: PropTypes.func,
    onProjectLoaded: PropTypes.func,
    onProjectSaved: PropTypes.func,
    onProjectsListLoaded: PropTypes.func,
    projectTitle: PropTypes.string,
    projects: PropTypes.array,
    saveProjectSb3: PropTypes.func,
    vm: PropTypes.instanceOf(VM),
};

const mapStateToProps = (state) => ({
    projectTitle: state.scratchGui.projectTitle,
    saveProjectSb3: state.scratchGui.vm.saveProjectSb3.bind(
        state.scratchGui.vm,
    ),
    vm: state.scratchGui.vm,
    authLoading: state.scratchGui.magblocksAuth.authLoading,
    isLoggedIn: state.scratchGui.magblocksAuth.isLoggedIn,
    user: state.scratchGui.magblocksAuth.user,
    currentProjectId: state.scratchGui.magblocksProject.currentProjectId,
    currentProjectName: state.scratchGui.magblocksProject.currentProjectName,
    projects: state.scratchGui.magblocksProject.projects,
    projectChanged: state.scratchGui.projectChanged,
});

const mapDispatchToProps = (dispatch) => ({
    onClickNew: () => {
        dispatch(requestNewProject(false));
        dispatch(setFileHandle(null));
        dispatch(clearCurrentProject());
    },
    onProjectSaved: (id, name) => {
        dispatch(setCurrentProject(id, name));
        dispatch(setProjectUnchanged());
    },
    onProjectLoaded: (id, name) => {
        dispatch(setCurrentProject(id, name));
        dispatch(setProjectUnchanged());
        dispatch(setProjectTitle(name));
    },
    onSetAuthUser: (user) =>
        dispatch({ type: "magblocks/SET_AUTH_USER", user }),
    onSignOut: () => {
        dispatch({ type: "magblocks/SET_AUTH_USER", user: null });
        dispatch(clearCurrentProject());
    },
    onProjectsListLoaded: (projects) => dispatch(setProjectsList(projects)),
    onProjectDeleted: (id) => dispatch(removeProject(id)),
});

export default compose(
    injectIntl,
    MenuBarHOC,
    connect(mapStateToProps, mapDispatchToProps),
)(MagblocksMenuBar);
